import { E_ChartType } from "@/utils/chart";
import { IPageData, IPageParams } from "../types";
import { T_Lang } from "@/i18n/types";
import { E_FaultType } from "./fault.types";
import { IEquipmentNums } from "./largeScreen.types";
import { E_AlarmLevel, E_AlarmType } from "../enums";
import { E_OnlineOffline } from "./overview.types";
import { E_ModelFaultType } from "./diagnosis.types";
import { E_CompanyLogoStatus } from "./platformCompany.types";

export enum E_WeeklyReportSourceType {
    /**
     * 系统生成
     */
    SystemGenerate = 0,
    /**
     * 自己上传图片
     */
    Manual = 1,
    /**
     * 啥都没
     */
    None = 2,
}

/**
 * $t('enum.E_WeekReportDealStatus.Handled')
 * $t('enum.E_WeekReportDealStatus.Unhandled')
 */
export enum E_WeekReportDealStatus {
    Handled = 'Handled',
    Unhandled = 'Unhandled',
}

/**
 * 设备状态
 * $t('enum.E_WeeklyReportEquipmentStatus.None')
 * $t('enum.E_WeeklyReportEquipmentStatus.Normal')
 * $t('enum.E_WeeklyReportEquipmentStatus.Alarm')
 * $t('enum.E_WeeklyReportEquipmentStatus.Halt')
 * $t('enum.E_WeeklyReportEquipmentStatus.Offline')
 */
export enum E_WeeklyReportEquipmentStatus {
    /**
     * 没有
     */
    None = 0,
    /**
     * 正常
     */
    Normal = 1,
    /**
     * 报警
     */
    Alarm = 2,
    /**
     * 停机
     */
    Halt = 3,
    /**
     * 离线
     */
    Offline = 4,
}

/**
 * 严重等级
 * $t('enum.E_WeeklyReportSeverityLevel.Normal')
 * $t('enum.E_WeeklyReportSeverityLevel.Minor')
 * $t('enum.E_WeeklyReportSeverityLevel.Severity')
 * $t('enum.E_WeeklyReportSeverityLevel.Offline')
 */
export enum E_WeeklyReportSeverityLevel {
    /**
     * 正常
     */
    Normal = 1,
    /**
     * 较严重
     */
    Minor = 2,
    /**
     * 严重
     */
    Severity = 3,
    /**
     * 离线
     */
    Offline = 4,
}


export interface IWithUploadImage {
    fileId: number
    fileUrl: string
    sourceType: E_WeeklyReportSourceType
}

export interface IWeeklyReportListItem {
    /**
     * 采集时间
     */
    collectTime: string
    /**
     * 公司Id
     */
    companyId: number
    /**
     * 公司名称
     */
    companyName: string
    id: number
    /**
     * 月周期
     */
    monthCycle: string
    /**
     * 总公司Id
     */
    parentCompanyId: number
    /**
     * 报告名称
     */
    reportName: string

    reportSn: string


    version: '1.0' | '2.0'
}

export interface IWeeklyReportListPageParams extends IPageParams {
    companyId: string
    version?: '1.0' | '2.0'
}

export type IWeeklyReportListItemData = IPageData<IWeeklyReportListItem>

export interface IWeeklyReportHistoryPageParams {
    /**
     * 周报列表的ID
     */
    reportId: number
}

export interface IWeeklyReportHistoryPageItem {
    id: number
    createTime: string
    reportId: number
    reportName: string
    source: number
    updatedTime: string
    reportSn: string
}

export type IWeeklyReportHistoryPageData = IWeeklyReportHistoryPageItem[]

/**
 * 区域报警
 */
export interface IWeeklyReportAreaAlarmItem extends IWithUploadImage {
    apercentage?: number
    bpercentage?: number
    cpercentage?: number
    dpercentage?: number

    anum?: number
    bnum?: number
    cnum?: number
    dnum?: number

    areaId?: number
    areaName?: string
    companyId?: number
    companyName?: string
    createTime?: string
    updateTime?: string
    historyId?: number
    id?: number
    isDel?: number
    parentCompanyId?: number
    sort: number
    title: string
}

export interface IWeekWaveDetail {
    chartType: E_ChartType
    maps: {
        interval: number
        wave: {
            x: number
            y1: number
            y2: number
            y3: number
        }[]
    }
}

export interface IWeekWaveVO {
    waveId: number
    createTime: string
    mappingName: string
    /**
     * 传感器名称
     */
    sensorId: string
    waveDetail: IWeekWaveDetail[]
}


/**
 * 设备统计
 */
export interface IWeeklyReportEquipRecordItem {
    areaId: number
    areaName: string
    companyId: number
    companyName: string
    createTime: string
    updateTime: string

    /**
     * 电流分析诊断，正常、报警、停机、离线
     */
    currentStatus: E_WeeklyReportEquipmentStatus

    /**
     * 波峰因素分析诊断
     */
    peakStatus: E_WeeklyReportEquipmentStatus

    /**
     * 峭度分析诊断
     */
    steepStatus: E_WeeklyReportEquipmentStatus

    /**
     * 温度分析诊断，正常、报警、停机、离线
     */
    temStatus: E_WeeklyReportEquipmentStatus
    /**
     * 振动分析诊断
     */
    vibrateStatus: E_WeeklyReportEquipmentStatus

    /**
     * 诊断结论
     */
    diagnosis: string

    equipmentId: number
    equipmentName: string

    /**
     * 图片Id集合
     */
    fileIdList: number[]

    /**
     * 上传的图片Id集合
     */
    fileIds: number[]

    /**
     * 
     */
    fileUrl: string[]

    /**
     * 健康评分
     */
    healthScore: number

    historyId: number
    id: number
    /**
     * 检测周期
     */
    monitorTime: string
    /**
     * 总公司Id
     */
    parentCompanyId: number

    faultTypeIds?: string // array<E_FaultType>

    /**
     * 故障分析结论
     */
    suggest: string
    /**
     * 严重等级
     */
    scoreStatus: E_WeeklyReportSeverityLevel
    /**
     * 标题
     */
    title: string
    /**
     * 报警波形Id集合
     */
    waveIds: string
    /**
     * 波形描述
     */
    waveTitle: string
    /**
     * 波形加速度和频谱数据(保存时候不要存)
     */
    weekWave?: IWeekWaveVO[]
    /**
     * 波形Id集合
     */
    waveIdList: any[]
}

export interface IMonitorWeekCoAlarm extends IWithUploadImage {
    apercentage: number
    bpercentage: number
    cpercentage: number
    dpercentage: number

    anum: number
    bnum: number
    cnum: number
    dnum: number

    companyId: number
    companyName: string

    createTime: string
    updateTime: string

    title: string
    id: number
    historyId: number
}

export interface IMonitorWeekCoDiagnosis extends IWithUploadImage {
    title: string
    companyId: number
    companyName: string
    historyId: number
    id: number
    /**
     * 
     */
    parentCompanyId: number
    /**
     * 轴承故障占比
     */
    bearingPercentage: number
    bearingNum: number
    /**
     * 风机叶片摩擦占比
     */
    fanPercentage: number
    fanNum: number
    /**
     * 齿轮箱不对中占比
     */
    gearBPercentage: number
    gearBNum: number
    /**
     * 齿轮箱负载高占比
     */
    gearFPercentage: number
    gearFNum: number
    /**
     * 齿轮箱磨损占比
     */
    gearMPercentage: number
    gearMNum: number
    /**
     * 不平衡占比
     */
    imbalancePercentage: number
    imbalanceNum: number
    /**
     * 设备基础松动占比
     */
    loosenessPercentage: number
    loosenessNum: number
    /**
     * 不对中占比
     */
    misalignedPercentage: number
    misalignedNum: number

    /**
     * 水泵叶片摩擦占比
     */
    pumpPercentage: number
    pumpNum: number
    /**
     * 共振占比
     */
    resonancePercentage: number
    resonanceNum: number
    /**
     * 转定子气息不均占比
     */
    unevenBreathPercentage: number
    unevenBreathNum: number

    createTime: string
    updateTime: string
}

export interface IWeeklyReportDetail {
    /**
     * 项目概述
     */
    overview: string
    /**
     * 
     */
    historyId: number
    /**
     * 周报Id
     */
    reportId: number
    /**
     * 周报名称
     */
    reportName: string

    /**
     * 区域报警
     */
    areaAlarms: IWeeklyReportAreaAlarmItem[]
    /**
     * 周期
     */
    cycle: string
    equipRecords: IWeeklyReportEquipRecordItem[]
    monitorWeekCoAlarm: IMonitorWeekCoAlarm
    /**
     * 有可能没有
     */
    monitorWeekCoDiagnosis?: IMonitorWeekCoDiagnosis

    /**
     * 首页
     */
    weeklyCoverUrl?: string
    /**
     * 页眉
     */
    pageHeaderUrl?: string
    /**
     * 页脚
     */
    pageFooterUrl?: string
}

export interface IWeeklyReportDetailParams {
    languageType: T_Lang
    /**
     * 周报历史Id
     */
    historyId: number
    /**
     * 周报
     */
    reportSn: string
}

export type IWeeklyReportEdit = IWeekWaveDetail

export interface IWeeklyReportExportPdfParams {
    reportSn: string
    historyId: number
    // reportId: number
    redirectUrl: string
}


export interface IWeeklyReportDetailParams2 {
    historyId: number
    languageType: string
    reportSn: string
}


export interface IWeeklyAlarmEquipmentDTOItem {
    alarmMappingNum
    alarmNum
    calarmTemNum
    calarmVibrationNum
    dalarmTemNum
    dalarmVibrationNum
    dealStatus
    equipmentId
    equipmentName
    equipmentRunTime
    healthScore
    mappingNum
}

export interface IWeeklyAlarmSensorDTO {
    alarmType: E_AlarmType
    alarmLevel: E_AlarmLevel
    alarmValue
    areaId
    areaName
    battery
    equipmentId
    equipmentName
    isOnline: E_OnlineOffline
    lastTime
    sensorId
    signalHeart
}

export interface IWeeklySensorRunStatusDTO {
    batterySensorNum
    normalSensorNum
    offlineSensorNum

    alarmNum: number
    onlineNum: number
    sensorNum: number

    /**
     * 传感器低电量/离线报警
     */
    weeklyAlarmSensorDTOList: IWeeklyAlarmSensorDTO[]
}

export interface IAlarmEquipmentData {
    /**
     * 设备报警统计
     */
    weeklyAlarmEquipmentDTOList: IWeeklyAlarmEquipmentDTOItem[]

    weeklySensorRunStatusDTO: IWeeklySensorRunStatusDTO
}

export interface IWeeklyAlarmEquipmentAreaDTO {
    areaId
    areaName
    calarmEquipmentNum
    dalarmEquipmentNum
    equipmentNum
    normalEquipmentNum
    offlineEquipmentNum
}


export interface IWeeklyEquipmentDiagnosisDTO {
    faultType: string
    faultTypeCount: number
}

export interface IEquipmentScoreStatisticsVO extends IEquipmentNums {
    dayDate
}

export interface IWeeklyEquipmentHealthScoreDTO extends IEquipmentNums {
    equipmentNum1
    equipmentNum2
    equipmentNum3
    equipmentScoreStatisticsVOList: IEquipmentScoreStatisticsVO[]
}

export interface IWeeklyEquipmentRunStatusDTO {
    alarmEquipmentNum
    calarmEquipmentNum
    dalarmEquipmentNum
    equipmentNum
    equipmentType
    equipmentTypeId
    normalEquipmentNum
}

/**
 * 设备运行状态
 */
export interface IEquipmentStatusData {
    weeklyAlarmEquipmentAreaDTOList: IWeeklyAlarmEquipmentAreaDTO[]
    weeklyEquipmentDiagnosisDTOList: IWeeklyEquipmentDiagnosisDTO[]
    weeklyEquipmentHealthScoreDTOList: IWeeklyEquipmentHealthScoreDTO
    weeklyEquipmentRunStatusDTOList: IWeeklyEquipmentRunStatusDTO[]
}

/**
 * 重点关注设备
 */
export interface ImportantEquipmentDTOItem {
    areaId
    areaName
    equipmentId
    equipmentName
    healthScore
}

export interface IMonitorWeekEquipDiagnosisDetailsDTO {
    aiSuggest
    alarmId
    alarmLevel
    alarmType
    areaId
    areaName
    companyId
    companyName
    diagnosis
    editDiagnosis
    editSuggest
    equipmentId
    equipmentName
    healthScore
    runTime
    suggestTypes
    waveId
    faultId
    faultTypes: E_ModelFaultType[]
    mappingId: number
    fileIdUrls: string[]
    fileIds: string
}

export interface IWeeklyReportDetailData2 {

    companyLogoStatus: E_CompanyLogoStatus

    /**
     * 
     */
    historyId: number
    /**
     * 周报Id
     */
    reportId: number
    /**
     * 周报名称
     */
    reportName: string

    /**
     * 周期
     */
    cycle: string

    /**
     * 首页
     */
    weeklyCoverUrl?: string
    /**
     * 页眉
     */
    pageHeaderUrl?: string
    /**
     * 页脚
     */
    pageFooterUrl?: string

    pageHeaderDesc?: string

    pageFooterDesc?: string

    companyLogoUrl?: string

    /**
     * 项目简述
     */
    projectDescription?: string
    /**
     * 	监测异常设备汇总表
     */
    alarmEquipment?: IAlarmEquipmentData
    /**
     * 	设备运行状态
     */
    equipmentStatus?: IEquipmentStatusData

    /**
     * 重点关注设备
     */
    importantEquipmentDTOList: ImportantEquipmentDTOItem[]
    /**
     * 设备异常诊断接口
     */
    equipDiagnosisDetailsDTOList: IMonitorWeekEquipDiagnosisDetailsDTO[]
}

export interface IEquipDiagnosisEditVOItem {
    diagnosis
    equipmentId
    fileIds: number[]
    suggest
}

export interface IWeeklyReportEditParams2 {
    historyId
    projectDescription
    reportId
    equipDiagnosisEditVOList: IEquipDiagnosisEditVOItem[]
}